// ** React Imports
import { useCallback, useEffect } from 'react';

// ** Third Party Components
import classnames from 'classnames';

// ** Store & Actions
import { ContentWidth, useLayoutContext } from '@data/store/LayoutProvider';

// ** Styles
import 'animate.css/animate.css';
import { RouteEntry } from '@src/@data/router/routeEntries';

interface LayoutWrapperProps {
  appLayout?: boolean;
  wrapperClass?: string;
  transition: string;
  routeMeta: RouteEntry['meta'];
}

const LayoutWrapper: React.FC<LayoutWrapperProps> = ({
  children,
  appLayout,
  wrapperClass,
  transition,
  routeMeta,
}) => {
  const { contentWidth, setContentWidth, setMenuCollapsed, setMenuHidden } =
    useLayoutContext();

  // ** Clean Up Function
  const cleanUp = useCallback(() => {
    if (routeMeta) {
      if (routeMeta?.contentWidth) {
        setContentWidth(ContentWidth.full);
      }
      if (routeMeta?.menuCollapsed) {
        setMenuCollapsed(!routeMeta.menuCollapsed);
      }
      if (routeMeta?.menuHidden) {
        setMenuHidden(!routeMeta.menuHidden);
      }
    }
  }, [routeMeta, setContentWidth, setMenuCollapsed, setMenuHidden]);

  // ** ComponentDidMount
  useEffect(() => {
    if (routeMeta) {
      if (routeMeta?.contentWidth) {
        setContentWidth(routeMeta.contentWidth);
      }
      if (routeMeta?.menuCollapsed) {
        setMenuCollapsed(routeMeta.menuCollapsed);
      }
      if (routeMeta?.menuHidden) {
        setMenuHidden(routeMeta.menuHidden);
      }
    }
    return () => cleanUp();
  }, [cleanUp, routeMeta, setContentWidth, setMenuCollapsed, setMenuHidden]);

  return (
    <div
      className={classnames('app-content content overflow-hidden', {
        wrapperClass,
      })}
    >
      <div className="content-overlay"></div>
      <div className="header-navbar-shadow" />
      <div
        className={classnames({
          'content-wrapper': !appLayout,
          'content-area-wrapper': appLayout,
          'container p-0': contentWidth === 'boxed',
          [`animate__animated animate__${transition}`]:
            transition !== 'none' && transition.length,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default LayoutWrapper;
